import pLimit from 'p-limit'

export default class GridQueueHelper {
  constructor({ bulkCreateBatchSize, uploadId, updateSaveButtonState, gridApi, maxConcurrentCreateRequests, valuesUrl, addZendeskSpacerRows }) {
    this.updateQueue = []
    this.debounceDelay = 200

    this.bulkCreateBatchSize = bulkCreateBatchSize
    this.uploadId = uploadId
    this.updateSaveButtonState = updateSaveButtonState
    this.gridApi = gridApi
    this.isProcessing = false
    this.processingTimeout = null
    this.valuesUrl = valuesUrl
    this.addZendeskSpacerRows = addZendeskSpacerRows
    this.pendingRequestCount = 0

    this.limit = pLimit(maxConcurrentCreateRequests)

    // Track active requests for cancellation
    this.activeRequests = new Set()
  }

  triggerQueueProcessing() {
    if (this.processingTimeout) {
      clearTimeout(this.processingTimeout)
    }
    this.processingTimeout = setTimeout(() => {
      if (!this.isProcessing) {
        this.processQueue()
      }
    }, this.debounceDelay)
  }

  addItemToQueue(item) {
    item.updatedAt = new Date().toISOString()

    this.updateQueue.push(item)
    this.updateSaveButtonState()
    this.triggerQueueProcessing()

    this.updateFooterRow(item.unit_count, item.old_unit_count, item.field)
  }

  hasQueueItems() {
    return this.updateQueue.length > 0 || this.isProcessing || this.pendingRequestCount > 0
  }

  updateLoadingCount() {
    const totalPending = this.updateQueue.length + this.pendingRequestCount
    document.getElementById('loading-count').textContent = totalPending
  }

  updateFooterRow(newValue, oldValue, field) {
    const totals = this.gridApi.getPinnedBottomRow(this.addZendeskSpacerRows ? 2 : 0).data

    const safeOldValue = isFinite(oldValue) ? oldValue : 0
    const safeNewValue = isFinite(newValue) ? newValue : 0

    totals[field] = (
      parseFloat(totals[field] || 0) +
      parseFloat(safeNewValue || 0) -
      parseFloat(safeOldValue || 0)
    ).toFixed(2)

    const spacerRows = Array.from({ length: 2 }, () => ({ isSpacerRow: true }))
    this.gridApi.setGridOption('pinnedBottomRowData', [...spacerRows, totals])
  }

  async processQueue() {
    if (this.isProcessing) return
    this.isProcessing = true
    document.getElementById('loading-count').parentElement.classList.remove('d-none')
    this.updateSaveButtonState()

    const batchPromises = []

    // Create batches and promises while items are available
    while (this.updateQueue.length > 0) {
      const batch = this.updateQueue.splice(0, this.bulkCreateBatchSize)
      this.pendingRequestCount += batch.length
      this.updateLoadingCount()

      const promise = this.limit(() => this.processBatch(batch))
      batchPromises.push(promise)
    }

    try {
      // Wait for all batches to complete
      await Promise.all(batchPromises)
    } catch (error) {
      // Individual batch errors are handled in processBatch
      console.error('Error in queue processing:', error)
    }

    this.isProcessing = false
    this.updateSaveButtonState()
    this.checkProcessingStatus()
  }

  async processBatch(batch) {
    const abortController = new AbortController()
    this.activeRequests.add(abortController)

    const headers = {
      'Content-Type': 'application/json'
    }

    const csrfToken = document.querySelector('[name="csrf-token"]')
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken.content
    }

    try {
      const response = await fetch(
        `${this.valuesUrl}/submissions/${this.uploadId}/temp_service_records/batch_create`,
        {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            service_record_grids_temp_service_records: batch.map(item => ({
              customer_id: item.customer_id,
              client_care_relationship_id: item.client_care_relationship_id,
              employee_id: item.employee_id,
              program_id: item.program_id,
              service_funding_source_id: item.service_funding_source_id,
              provider_id: item.provider_id,
              location_provider_id: item.location_provider_id,
              service_definition_id: item.service_definition_id,
              service_delivered_on: item.service_delivered_on,
              service_record_id: item.service_record_id,
              unit_count: item.unit_count,
              last_client_updated_at: item.updatedAt
            }))
          }),
          signal: abortController.signal
        }
      )

      const data = await response.json()

      this.pendingRequestCount -= batch.length
      this.updateLoadingCount()

      if (data.success) {
        batch.forEach(item => {
          item.node.data[`days_${item.day}_is_temp`] = true
          item.node.data[item.field] = item.unit_count
        })

        const uniqueNodes = [...new Set(batch.map(item => item.node))]
        const uniqueFields = [...new Set(batch.map(item => item.field))]

        requestAnimationFrame(() => {
          this.gridApi.refreshCells({
            rowNodes: uniqueNodes,
            columns: uniqueFields,
            force: true
          })
        })
      } else {
        document.dispatchEvent(
          new CustomEvent('toast', {
            detail: { failure: true, message: data['error_messages'] }
          })
        )
      }
    } catch (error) {
      this.pendingRequestCount -= batch.length
      this.updateLoadingCount()

      if (error.name !== 'AbortError') {
        console.error('Error processing batch:', error)
      }
    } finally {
      this.activeRequests.delete(abortController)
    }
  }

  checkProcessingStatus() {
    // If we have more items in queue, continue processing
    if (this.updateQueue.length > 0) {
      setTimeout(() => this.processQueue(), 0)
    }
    // If no more items in queue but still have pending requests, wait
    else if (this.pendingRequestCount > 0) {
      // Don't hide loading indicator yet, let the promise callbacks handle it
    }
    // If no more items and no pending requests, hide loading indicator
    else {
      document.getElementById('loading-count').parentElement.classList.add('d-none')
    }
  }
}
